import React, { ComponentProps, useEffect, useState } from 'react';
import { ModalFooter, Button, usePaginationSkipper, UiKit } from '@datavant/uikit';
import { ChakraProvider, VStack, useToast, Box, Link } from '@chakra-ui/react';
import { AddressConfirmation } from './AddressConfirmation';
import { trpc, tRPCProxyClient } from '../../../../utils/trpc';
import { AddressConfirmationTableRow } from './Columns';
import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';
import { NonEmbeddedImplementationStatus } from '../../../../generated/request-manager-api';
import { NoAddressesOverlay } from './NoAddressesOverlay';
import NextLink from 'next/link';
import { Modal, Text } from '@datavant/dart';

export const AddressConfirmationModal = ({
    isOpen,
    onClose,
}: {
    isOpen: boolean;
    onClose: ComponentProps<typeof Modal>['onClose'];
}) => {
    const toast = useToast();
    const [isEditing, setIsEditing] = useState(false);
    const [data, setData] = useState<AddressConfirmationTableRow[]>([]);
    const [autoResetPageIndex, skipAutoResetPageIndex] = usePaginationSkipper();
    const utils = trpc.useUtils();

    const {
        data: facilityAddresses,
        refetch,
        isFetching,
    } = trpc.autofulfill.getAutofulfillAddresses.useQuery();

    const { mutate, isLoading: isMutating } = trpc.autofulfill.addAutofulfillAddressFeedback.useMutation({
        onMutate: async () => {
            await utils.tenant.getTenant.cancel();
            const previousTenantData = utils.tenant.getTenant.getData();
            utils.tenant.getTenant.setData(undefined, old =>
                old && old !== null && old.digitalMetadata
                    ? { ...old, digitalMetadata: { ...old.digitalMetadata, addressFeedbackComplete: true } }
                    : null
            );

            // Return a context object with the snapshotted value so it can be used to reset the cache to a previous state
            // on error
            return { previousTenantData };
        },
        onSuccess: async () => {
            toast({
                title: 'Addresses confirmed.',
                status: 'success',
                duration: 2000,
                isClosable: true,
            });
            await tRPCProxyClient.autofulfill.updateOnboardingStatus.mutate({
                status: NonEmbeddedImplementationStatus.ADDRESS_FEEDBACK_COMPLETED,
            });
            onClose();
        },
        onError: async () => {
            // Force a re-fetch of the tenant data on error
            await utils.tenant.getTenant.invalidate();

            toast({
                title: 'There was an error confirming addresses. Please try again or reach out to customer service via help.',
                status: 'error',
                duration: 2000,
                isClosable: true,
            });
        },
    });

    const isLoading = isFetching || isMutating;

    const excludeRow = (addressId: string) => {
        skipAutoResetPageIndex();
        setData(data => {
            const rowIndex = data.findIndex(({ id }) => id === addressId);
            if (rowIndex < 0) {
                datadogLogs.logger.error('no address found with this id', { addressId });
                datadogRum.addError('no address found with this id', { addressId });
                return data;
            }
            return [
                ...data.slice(0, rowIndex),
                { ...data[rowIndex], excluded: true },
                ...data.slice(rowIndex + 1),
            ];
        });
    };

    useEffect(() => {
        setData(
            (facilityAddresses ?? []).map(row => ({
                ...row,
                excluded: false,
                excludeRow,
            }))
        );
    }, [facilityAddresses, isFetching]);

    return (
        <ChakraProvider>
            <Modal
                opened={isOpen}
                onClose={onClose}
                size="xl"
                title={<Text size={'xl'}>Verify the Sites for AutoFulfill Routing</Text>}
            >
                <VStack alignItems={'stretch'} spacing={5} pb={0}>
                    <Box>
                        <VStack gap={'16px'} alignItems={'flex-start'}>
                            <Text size={'sm'}>
                                Please confirm whether the addresses in Request Manager should be used for
                                Auto-Fulfillment setup. To update or add a new address, go to
                                <Link
                                    as={NextLink}
                                    href="/settings/sites"
                                    color={UiKit.consistency.primaryBlue50}
                                    isExternal={true}
                                >
                                    {' '}
                                    Settings {'>'} Sites.
                                </Link>
                            </Text>
                            <Text size={'sm'}>
                                Please note that updating or excluding addresses may delay the activation
                                timeline for AutoFulfill.
                            </Text>
                        </VStack>
                        <AddressConfirmation
                            data={data}
                            isLoading={isLoading}
                            shouldShowAction={isEditing}
                            autoResetPageIndex={autoResetPageIndex}
                        />
                        {!isLoading && data.length === 0 && <NoAddressesOverlay />}
                    </Box>
                    <ModalFooter
                        action={
                            <Button
                                intent={!isEditing ? 'prominent' : 'negative'}
                                isLoading={isLoading}
                                onClick={() => mutate(data)}
                                isDisabled={!data.length}
                            >
                                {!isEditing ? 'Confirm Addresses' : 'Confirm Address Changes'}
                            </Button>
                        }
                        cancel={
                            !isEditing ? (
                                <Button
                                    buttonAppearance={'ghost'}
                                    intent={'negative'}
                                    onClick={() => setIsEditing(true)}
                                    isLoading={isLoading}
                                    // users must have at least two sites to make modifications
                                    isDisabled={data.length < 2}
                                >
                                    Modify Addresses for Autofulfill
                                </Button>
                            ) : (
                                <></>
                            )
                        }
                        back={
                            <Button
                                buttonAppearance={'ghost'}
                                intent={'neutral'}
                                onClick={() => {
                                    if (!isEditing) {
                                        onClose();
                                    } else {
                                        setIsEditing(false);
                                        void refetch();
                                    }
                                }}
                            >
                                {!isEditing ? 'Close' : 'Back to Default Settings'}
                            </Button>
                        }
                    />
                </VStack>
            </Modal>
        </ChakraProvider>
    );
};
